import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
  MoveInfo,
} from "..\/..\/interfaces\/DecisionPriority";
import { MCTSMoveGenerationService } from "..\/..\/..\/utils\/MCTSMoveGenerationService";
import {
  MCTSUtils_v2,
  cloneFullGameState,
  getPieceFromPosition,
  applyMoveWithSnakeRespawn,
  validateMeadowEffects,
} from "..\/..\/..\/utils\/MCTSUtils";
import { MoveType, GameState, IPiece, Position } from "..\/..\/..\/..\/types";

/**
 * Tactical Pattern Recognition for Free Threats and Dominance
 */
interface TacticalFeature {
  type: "freeTheat" | "fork" | "pin" | "skewer" | "outpost" | "dominance";
  score: number; // 0.0 to 1.0 tactical strength
  description: string;
}

interface TacticalPatternResult {
  features: TacticalFeature[];
  totalScore: number;
  hasCriticalPattern: boolean;
}

class TacticalPatternRecognizer {
  /**
   * Analyze a move for tactical patterns and assign a tactical prior score
   */
  static recognizePatterns(
    move: MoveInfo,
    gameState: GameState,
    resultingGameState?: GameState
  ): TacticalPatternResult {
    const features: TacticalFeature[] = [];

    // Free Threat Detection (Fungi vs Octopus, etc.)
    const freeTheatFeature = this.detectFreeTheat(
      move,
      gameState,
      resultingGameState
    );
    if (freeTheatFeature) {
      features.push(freeTheatFeature);
    }

    // TODO: Add more tactical patterns
    // const forkFeature = this.detectFork(move, gameState, resultingGameState);
    // const pinFeature = this.detectPin(move, gameState, resultingGameState);

    const totalScore = features.reduce((sum, f) => sum + f.score, 0);
    const hasCriticalPattern = features.some((f) => f.score >= 0.8);

    return {
      features,
      totalScore: Math.min(totalScore, 1.0), // Cap at 1.0
      hasCriticalPattern,
    };
  }

  /**
   * Detect free threat patterns (one-sided tactical advantages)
   * Example: Fungi threatening Octopus diagonally (Octopus has no counterplay)
   */
  private static detectFreeTheat(
    move: MoveInfo,
    gameState: GameState,
    resultingGameState?: GameState
  ): TacticalFeature | null {
    const movingPiece = getPieceFromPosition(gameState, move.fromPosition);
    if (!movingPiece) return null;

    // Fungi vs Octopus Pattern
    if (movingPiece.type === "Fungi") {
      return this.detectFungiVsOctopusPattern(
        move,
        gameState,
        resultingGameState
      );
    }

    // TODO: Add more free threat patterns
    // - Bear vs Crane/Penguin/Goat (immunity)
    // - Rhino vs Crane/Penguin/Goat (immunity)
    // - Wolf vs Crane/Penguin (immunity)

    return null;
  }

  /**
   * Specific pattern: Fungi threatening Octopus diagonally
   * Fungi cannot be entangled, Octopus can only capture orthogonally
   */
  private static detectFungiVsOctopusPattern(
    move: MoveInfo,
    gameState: GameState,
    resultingGameState?: GameState
  ): TacticalFeature | null {
    const newPosition = move.move.position;

    // Check if new position threatens an enemy octopus diagonally
    const diagonalDirections = [
      { row: -1, col: -1 },
      { row: -1, col: 1 },
      { row: 1, col: -1 },
      { row: 1, col: 1 },
    ];

    for (const dir of diagonalDirections) {
      const targetRow = newPosition.row + dir.row;
      const targetCol = newPosition.col + dir.col;

      if (targetRow >= 0 && targetRow < 8 && targetCol >= 0 && targetCol < 5) {
        const targetSquare = gameState.board[targetRow][targetCol];

        if (targetSquare.Pieces.length > 0) {
          const targetPiece = targetSquare.Pieces[0];

          if (
            targetPiece.type === "Octopus" &&
            targetPiece.isLight !== gameState.isLightTurn
          ) {
            // Verify this is truly a free threat
            const isSafeThreat = this.isSafeFungiThreat(
              newPosition,
              { row: targetRow, col: targetCol },
              gameState
            );

            if (isSafeThreat) {
              return {
                type: "freeTheat",
                score: 0.7, // High tactical value
                description: `Fungi dominance: Threatens Octopus diagonally with no counterplay`,
              };
            }
          }
        }
      }
    }

    return null;
  }

  /**
   * Verify that Fungi threatening Octopus is actually safe
   */
  private static isSafeFungiThreat(
    fungiPos: Position,
    octopusPos: Position,
    gameState: GameState
  ): boolean {
    // Check if Fungi is safe from immediate capture
    const moveService = MCTSMoveGenerationService.getInstance();

    // Switch to enemy perspective
    const testGameState = cloneFullGameState(gameState);
    testGameState.isLightTurn = !testGameState.isLightTurn;

    const enemyMoves = moveService.getAllMoves(testGameState);

    // Check if enemy can capture our Fungi
    for (const enemyMove of enemyMoves) {
      if (
        enemyMove.move.position.row === fungiPos.row &&
        enemyMove.move.position.col === fungiPos.col &&
        enemyMove.move.moveType === MoveType.capture
      ) {
        return false; // Not safe - can be captured
      }
    }

    // Check if Octopus has escape routes
    const octopusEscapes = this.countSafeEscapeRoutes(
      octopusPos,
      testGameState
    );

    // Free threat is stronger if Octopus has limited escape options
    return octopusEscapes <= 2; // Modest escape routes = stronger threat
  }

  /**
   * Count safe escape routes for a threatened piece
   */
  private static countSafeEscapeRoutes(
    piecePos: Position,
    gameState: GameState
  ): number {
    const moveService = MCTSMoveGenerationService.getInstance();
    const allMoves = moveService.getAllMoves(gameState);

    // Count moves available to the piece at this position
    const escapeMoves = allMoves.filter(
      (move) =>
        move.fromPosition.row === piecePos.row &&
        move.fromPosition.col === piecePos.col
    );

    return escapeMoves.length;
  }
}

/**
 * Priority 17: Free Capture & Tactical Pattern Recognition
 *
 * DUAL PURPOSE:
 * 1. Early exit for immediate free captures (guaranteed material advantage)
 * 2. Tactical pattern recognition for PUCT-based MCTS depth evaluation
 *
 * This file identifies tactical features like:
 * - Free threats (Fungi vs Octopus, Bear vs weak pieces)
 * - Forks, pins, skewers
 * - Outposts and dominance patterns
 *
 * MCTS can then seek these patterns at any depth using PUCT priors!
 */
export class FreeCapturePriority extends BaseDecisionPriority {
  readonly priority = 17;
  readonly name = "Free Capture & Tactical Recognition";
  readonly description =
    "Capture enemy pieces and recognize tactical patterns for MCTS";

  private analysisLogs: string[] = [];
  private tacticalScores: Map<string, number> = new Map(); // Move ID -> Tactical Score

  isRelevant(context: DecisionContext): boolean {
    return true; // Always check for free captures
  }

  analyze(context: DecisionContext): DecisionResult {
    this.analysisLogs = [];
    this.tacticalScores.clear();

    const gameState = context.gameState;

    // PHASE 1: Check for immediate free captures (early exit priority)
    const freeCapturesMoves = this.findFreeCapturesMovess(
      context.allPossibleMoves,
      gameState
    );

    // PHASE 2: Tactical pattern recognition for ALL moves (for MCTS priors)
    const tacticalAnalysis = this.analyzeTacticalPatterns(
      context.allPossibleMoves,
      gameState
    );

    // Early exit for immediate free captures
    if (freeCapturesMoves.length > 0) {
      const bestFreeCapture = this.selectBestFreeCapture(
        freeCapturesMoves,
        gameState
      );

      this.addLog(
        `✅ EARLY EXIT: Free capture [${bestFreeCapture.fromPosition.row},${bestFreeCapture.fromPosition.col}] → [${bestFreeCapture.move.position.row},${bestFreeCapture.move.position.col}] (${freeCapturesMoves.length} options)`
      );

      return this.createEarlyExitResult(
        bestFreeCapture,
        `Free capture - no recapture risk`,
        {
          logs: this.analysisLogs,
          freeCapturesFound: freeCapturesMoves.length,
          tacticalScores: Object.fromEntries(this.tacticalScores),
        }
      );
    }

    // Continue chain but provide tactical scores for MCTS
    if (tacticalAnalysis.tacticalMovesFound > 0) {
      this.addLog(
        `🎯 TACTICAL PATTERNS: Found ${tacticalAnalysis.tacticalMovesFound} moves with tactical features`
      );
      this.addLog(
        `📊 Best tactical score: ${tacticalAnalysis.bestTacticalScore.toFixed(
          2
        )} (${tacticalAnalysis.bestPattern})`
      );

      // Log specific tactical moves for debugging
      const tacticalMoves = Array.from(this.tacticalScores.entries()).slice(
        0,
        3
      );
      for (const [moveId, score] of tacticalMoves) {
        this.addLog(`🎲 Tactical move: ${moveId} (score: ${score.toFixed(2)})`);
      }

      // Pass all moves through but with tactical scoring data for MCTS
      return this.createFilterResult(
        context.allPossibleMoves, // Pass all moves through
        "Tactical patterns identified for MCTS evaluation",
        {
          logs: this.analysisLogs,
          tacticalScores: Object.fromEntries(this.tacticalScores),
          tacticalMovesFound: tacticalAnalysis.tacticalMovesFound,
          bestTacticalScore: tacticalAnalysis.bestTacticalScore,
        }
      );
    }

    // Enhanced logging for debugging
    this.addLog(`🔍 TACTICAL ANALYSIS SUMMARY:`);
    this.addLog(`  - Free captures found: ${freeCapturesMoves.length}`);
    this.addLog(
      `  - Tactical patterns found: ${tacticalAnalysis.tacticalMovesFound}`
    );
    this.addLog(`  - Total moves analyzed: ${context.allPossibleMoves.length}`);

    this.addLog("❌ No immediate free captures or tactical patterns found");
    return this.createNoActionResult("No tactical advantages detected");
  }

  private addLog(message: string): void {
    this.analysisLogs.push(message);
  }

  /**
   * Analyze all moves for tactical patterns and assign scores for MCTS priors
   */
  private analyzeTacticalPatterns(
    allMoves: MoveInfo[],
    gameState: GameState
  ): {
    tacticalMovesFound: number;
    bestTacticalScore: number;
    bestPattern: string;
  } {
    let tacticalMovesFound = 0;
    let bestTacticalScore = 0;
    let bestPattern = "none";

    this.addLog(
      `🔍 ANALYZING ${allMoves.length} moves for tactical patterns...`
    );

    for (const move of allMoves) {
      const tacticalResult = TacticalPatternRecognizer.recognizePatterns(
        move,
        gameState
      );

      // Log all moves with any tactical features for debugging
      if (tacticalResult.totalScore > 0) {
        tacticalMovesFound++;

        const moveId = `${move.fromPosition.row},${move.fromPosition.col}->${move.move.position.row},${move.move.position.col}`;
        this.tacticalScores.set(moveId, tacticalResult.totalScore);

        this.addLog(
          `✅ Found tactical pattern: ${moveId} (score: ${tacticalResult.totalScore.toFixed(
            2
          )}) - ${tacticalResult.features[0]?.description || "unknown"}`
        );

        if (tacticalResult.totalScore > bestTacticalScore) {
          bestTacticalScore = tacticalResult.totalScore;
          bestPattern =
            tacticalResult.features.length > 0
              ? tacticalResult.features[0].description
              : "tactical pattern";
        }
      }
    }

    if (tacticalMovesFound === 0) {
      this.addLog(
        `❌ No tactical patterns detected in any of the ${allMoves.length} moves`
      );
    }

    return {
      tacticalMovesFound,
      bestTacticalScore,
      bestPattern,
    };
  }

  /**
   * Find all moves that capture enemy pieces with no immediate recapture risk
   */
  private findFreeCapturesMovess(
    allMoves: MoveInfo[],
    gameState: any
  ): MoveInfo[] {
    const freeCapturesMovess: MoveInfo[] = [];
    let capturesChecked = 0;

    for (const moveInfo of allMoves) {
      // Check if this move captures an enemy piece
      const targetSquare =
        gameState.board[moveInfo.move.position.row][moveInfo.move.position.col];

      if (!targetSquare.Pieces || targetSquare.Pieces.length === 0) {
        continue; // No capture
      }

      const capturedPiece = targetSquare.Pieces[0];
      const isEnemyPiece = capturedPiece.isLight !== gameState.isLightTurn;

      if (!isEnemyPiece) {
        continue; // Not capturing an enemy piece
      }

      capturesChecked++;

      // Simulate the capture and check if enemy can recapture immediately
      const isSafeCapture = this.isCaptureWithoutRecapture(moveInfo, gameState);

      if (isSafeCapture) {
        freeCapturesMovess.push(moveInfo);
      }
    }

    this.addLog(
      `🔍 Checked ${capturesChecked} captures, found ${freeCapturesMovess.length} free captures`
    );

    return freeCapturesMovess;
  }

  /**
   * FIXED: Check if a capture can be made without immediate recapture
   * This was completely broken - it was only checking if enemy could MOVE to the square,
   * not if they could actually CAPTURE our piece there.
   */
  private isCaptureWithoutRecapture(
    captureMove: MoveInfo,
    gameState: any
  ): boolean {
    try {
      // Simulate making the capture
      const testGameState = cloneFullGameState(gameState);
      const ourPiece = getPieceFromPosition(
        testGameState,
        captureMove.fromPosition
      );

      if (!ourPiece) {
        return false;
      }

      // Manual capture simulation (more reliable than applyMoveWithSnakeRespawn)
      const targetSquare =
        testGameState.board[captureMove.move.position.row][
          captureMove.move.position.col
        ];
      const capturedPiece =
        targetSquare.Pieces.length > 0 ? targetSquare.Pieces[0] : null;

      if (!capturedPiece) {
        return false;
      }

      // Remove our piece from original position
      const fromSquare =
        testGameState.board[captureMove.fromPosition.row][
          captureMove.fromPosition.col
        ];
      fromSquare.Pieces = fromSquare.Pieces.filter((p) => p.id !== ourPiece.id);

      // Remove captured piece from target position
      targetSquare.Pieces = targetSquare.Pieces.filter(
        (p) => p.id !== capturedPiece.id
      );

      // Place our piece at target position
      targetSquare.Pieces.push(ourPiece);

      // Turn switch
      testGameState.isLightTurn = !testGameState.isLightTurn;

      validateMeadowEffects(testGameState);

      // Check if enemy can immediately capture our piece
      const moveService = MCTSMoveGenerationService.getInstance();
      const enemyMoves = moveService.getAllMoves(testGameState);

      const capturePosition = captureMove.move.position;
      let recaptureThreats = 0;

      // Count actual capture threats (not just moves to the position)
      for (const enemyMove of enemyMoves) {
        if (
          enemyMove.move.position.row === capturePosition.row &&
          enemyMove.move.position.col === capturePosition.col &&
          enemyMove.move.moveType === MoveType.capture
        ) {
          recaptureThreats++;
        }
      }

      return recaptureThreats === 0;
    } catch (error) {
      return false; // Assume unsafe if error occurs
    }
  }

  /**
   * Select the best free capture based on piece value and strategic importance
   */
  private selectBestFreeCapture(
    freeCapturesMovess: MoveInfo[],
    gameState: any
  ): MoveInfo {
    // Simple heuristic: prefer capturing higher value pieces

    let bestCapture = freeCapturesMovess[0];
    let bestScore = 0;

    for (const capture of freeCapturesMovess) {
      const targetSquare =
        gameState.board[capture.move.position.row][capture.move.position.col];
      const capturedPiece = targetSquare.Pieces[0];

      // Basic piece values (could be refined)
      let pieceValue = this.getPieceValue(capturedPiece.type);

      // Bonus for capturing kings
      if (capturedPiece.isKing) {
        pieceValue += 100; // Huge bonus for king captures
      }

      if (pieceValue > bestScore) {
        bestScore = pieceValue;
        bestCapture = capture;
      }
    }

    return bestCapture;
  }

  /**
   * Simple piece value assessment
   */
  private getPieceValue(pieceType: string): number {
    const values: { [key: string]: number } = {
      Bear: 1,
      Rhino: 1,
      Wolf: 1,
      Tree: 1,
      Octopus: 1,
      Fungi: 1,
      Snake: 1,
      Monkey: 1,
      Crane: 1,
      Goat: 1,
      Penguin: 1,
      Frog: 1, // All pieces equal value
    };

    return values[pieceType] || 1;
  }
}

